import { useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Drawer, EmptyState, KeyValueList, Spinner, StatusPill } from '../../../ui'
import { CaseBundle, CaseSend, DOCUMENT_LABEL, DocumentKind, LinkBadge, ReplyListItem, caseStatusTone, deadlineTone, formatDate, formatDateTime, humanise, openSendDocument, sendStatusTone } from './shared'
import styles from './styles.module.scss'

type TimelineItem = { kind: 'sent'; at: string | null; send: CaseSend } | { kind: 'received'; at: string | null; reply: ReplyListItem }

/**
 * Everything on one claim: identity, comms case(s), and a merged timeline of
 * the LOCs we sent and the emails we received. Opened from the inbox detail.
 */
export const CaseDrawer = ({
    claimRef,
    currentReplyId,
    onClose,
    onSelectReply,
    onOpenSend,
    onShowAllEmails,
}: {
    claimRef: string | null
    currentReplyId: string | null
    onClose: () => void
    onSelectReply: (replyId: string) => void
    onOpenSend: (sendId: string) => void
    onShowAllEmails: (claimRef: string) => void
}) => {
    const [bundle, setBundle] = useState<CaseBundle | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [opening, setOpening] = useState<string | null>(null)

    useEffect(() => {
        if (!claimRef) {
            setBundle(null)
            setError(null)
            return
        }
        let cancelled = false
        setLoading(true)
        setError(null)
        api({ url: 'comms/inbox/case', data: { claimRef } })
            .then((resp) => {
                if (cancelled) return
                if (resp.data?.success) setBundle(resp.data)
                else setError(resp.data?.message || 'Could not load the case.')
            })
            .catch((err: any) => {
                if (cancelled) return
                const message = err?.response?.data?.message || 'Could not load the case.'
                setError(message)
                if (err?.response?.status !== 404) Report.failure('Case', message, 'Okay')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [claimRef])

    const timeline = useMemo<TimelineItem[]>(() => {
        if (!bundle) return []
        const items: TimelineItem[] = [
            ...bundle.sends.map((send) => ({ kind: 'sent' as const, at: send.sentAt || send.queuedAt, send })),
            ...bundle.replies.map((reply) => ({ kind: 'received' as const, at: reply.receivedAt, reply })),
        ]
        return items.sort((a, b) => (new Date(b.at || 0).getTime() || 0) - (new Date(a.at || 0).getTime() || 0))
    }, [bundle])

    const openDoc = async (sendId: string, kind: DocumentKind) => {
        setOpening(`${sendId}:${kind}`)
        await openSendDocument(sendId, kind)
        setOpening(null)
    }

    const identity = bundle?.identity ?? null
    const lenderNames = bundle ? Array.from(new Set(bundle.sends.map((s) => s.lenderName).filter((n): n is string => Boolean(n)))) : []

    return (
        <Drawer
            open={Boolean(claimRef)}
            onClose={onClose}
            width="wide"
            busy={loading}
            title={
                <span className={styles.mono} style={{ fontSize: 'inherit' }}>
                    {claimRef}
                </span>
            }
            subtitle={bundle ? [identity?.clientName, lenderNames.join(', ')].filter(Boolean).join(' · ') || undefined : undefined}
            actions={
                claimRef ? (
                    <Button size="sm" onClick={() => onShowAllEmails(claimRef)} title="Filter the inbox list to every email on this claim">
                        Show in list
                    </Button>
                ) : undefined
            }
        >
            {loading && !bundle ? (
                <Spinner block text="Loading case…" />
            ) : error && !bundle ? (
                <EmptyState bare title="Nothing held for this claim" description={error} />
            ) : bundle ? (
                <div className={styles.stack}>
                    <section>
                        <h3 className={styles.listTitle}>Client</h3>
                        {identity ? (
                            <KeyValueList
                                columns
                                items={[
                                    { label: 'Name', value: identity.clientName },
                                    { label: 'Postcode', value: identity.clientPostcode },
                                    { label: 'Email', value: identity.clientEmail, mono: true },
                                    { label: 'Agreement no.', value: identity.agreementNumber, mono: true },
                                    { label: 'Agreement start', value: formatDate(identity.agreementStartDate) },
                                    { label: 'Vehicle', value: identity.vehicle },
                                    { label: 'Registration', value: identity.vehicleRegistration, mono: true },
                                    { label: 'Proclaim ref', value: identity.proclaimReference, mono: true },
                                    { label: 'Introducer', value: identity.introducer },
                                    { label: 'Lender (record)', value: identity.lenderName },
                                ]}
                            />
                        ) : (
                            <Banner tone="warn">No identity snapshot for this claim yet — run the Case identity stage on the Pipeline page.</Banner>
                        )}
                        {identity?.snapshotError && <Banner tone="bad">Snapshot error: {identity.snapshotError}</Banner>}
                    </section>

                    <section>
                        <h3 className={styles.listTitle}>
                            Comms case{bundle.cases.length === 1 ? '' : 's'}
                            <StatusPill round size="sm">
                                {bundle.cases.length}
                            </StatusPill>
                        </h3>
                        {bundle.cases.length === 0 ? (
                            <span className={styles.mutedInline}>No comms_case row yet (created by the Case identity stage).</span>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Lender</th>
                                        <th>Status</th>
                                        <th>Opened</th>
                                        <th>Ack due</th>
                                        <th>Substantive due</th>
                                        <th>Lender refs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bundle.cases.map((c) => {
                                        const closed = Boolean(c.closedAt)
                                        return (
                                            <tr key={c.id}>
                                                <td>{c.lenderNormalisedName || '—'}</td>
                                                <td>
                                                    <StatusPill size="sm" tone={caseStatusTone(c.status)} dot title={c.closedReason || undefined}>
                                                        {humanise(c.status)}
                                                    </StatusPill>
                                                </td>
                                                <td>{formatDate(c.openedAt)}</td>
                                                <td>
                                                    <StatusPill size="sm" tone={deadlineTone(c.ackDeadline, closed)}>
                                                        {formatDate(c.ackDeadline)}
                                                    </StatusPill>
                                                </td>
                                                <td>
                                                    <StatusPill size="sm" tone={deadlineTone(c.substantiveDeadline, closed)}>
                                                        {formatDate(c.substantiveDeadline)}
                                                    </StatusPill>
                                                </td>
                                                <td className={styles.mono}>{c.lenderRefs.join(', ') || '—'}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )}
                    </section>

                    <section>
                        <h3 className={styles.listTitle}>
                            Timeline
                            <StatusPill round size="sm">
                                {timeline.length}
                            </StatusPill>
                            <span className={styles.mutedInline}>
                                {bundle.sends.length} sent · {bundle.replies.length} received
                            </span>
                        </h3>
                        {timeline.length === 0 ? (
                            <EmptyState bare title="No correspondence yet" />
                        ) : (
                            <ul className={styles.timeline}>
                                {timeline.map((item) =>
                                    item.kind === 'sent' ? (
                                        <li key={`s-${item.send.id}`} className={`${styles.timelineItem} ${styles.timelineSent}`}>
                                            <div className={styles.timelineTop}>
                                                <StatusPill size="sm" tone="info">
                                                    Sent
                                                </StatusPill>
                                                <StatusPill size="sm" tone={sendStatusTone(item.send.status)} dot>
                                                    {humanise(item.send.status)}
                                                </StatusPill>
                                                <span className={styles.mutedInline}>{formatDateTime(item.at)}</span>
                                            </div>
                                            <div className={styles.timelineSubject}>{item.send.subject || '(no subject)'}</div>
                                            <div className={styles.timelineMeta}>
                                                <span className={styles.mono}>→ {item.send.toEmail}</span>
                                                {item.send.footerRef && <span className={styles.mono}>{item.send.footerRef}</span>}
                                                {item.send.openCount > 0 && <span>opened {item.send.openCount}×</span>}
                                                {item.send.lastError && <span className={styles.mutedInline}>{item.send.lastError}</span>}
                                            </div>
                                            <div className={styles.actionRow}>
                                                <Button size="sm" onClick={() => onOpenSend(item.send.id)}>
                                                    Email
                                                </Button>
                                                {item.send.documents.map((kind) => (
                                                    <Button key={kind} size="sm" variant="ghost" loading={opening === `${item.send.id}:${kind}`} disabled={opening !== null} onClick={() => openDoc(item.send.id, kind)}>
                                                        {DOCUMENT_LABEL[kind]}
                                                    </Button>
                                                ))}
                                            </div>
                                        </li>
                                    ) : (
                                        <li key={`r-${item.reply.id}`} className={`${styles.timelineItem} ${styles.timelineReceived} ${item.reply.id === currentReplyId ? styles.timelineCurrent : ''}`}>
                                            <div className={styles.timelineTop}>
                                                <StatusPill size="sm" tone="accent">
                                                    Received
                                                </StatusPill>
                                                <LinkBadge state={item.reply.linkState} method={item.reply.matchMethod} confidence={item.reply.matchConfidence} />
                                                {item.reply.classification && (
                                                    <StatusPill size="sm" tone="info">
                                                        {humanise(item.reply.classification)}
                                                    </StatusPill>
                                                )}
                                                <span className={styles.mutedInline}>{formatDateTime(item.at)}</span>
                                            </div>
                                            <div className={styles.timelineSubject}>{item.reply.subject || '(no subject)'}</div>
                                            {item.reply.aiSummary && <div className={styles.replyItemSummary}>{item.reply.aiSummary}</div>}
                                            <div className={styles.timelineMeta}>
                                                <span className={styles.mono}>← {item.reply.fromEmail}</span>
                                                {item.reply.sourceMailbox && <span>{item.reply.sourceMailbox}</span>}
                                                {item.reply.attachmentCount > 0 && <span>{item.reply.attachmentCount} attachment(s)</span>}
                                            </div>
                                            <div className={styles.actionRow}>
                                                {item.reply.id === currentReplyId ? (
                                                    <span className={styles.mutedInline}>Currently open</span>
                                                ) : (
                                                    <Button size="sm" onClick={() => onSelectReply(item.reply.id)}>
                                                        Open email
                                                    </Button>
                                                )}
                                            </div>
                                        </li>
                                    )
                                )}
                            </ul>
                        )}
                    </section>
                </div>
            ) : null}
        </Drawer>
    )
}
